export type ScrollDirection = 'forward' | 'backward';

export interface VisibleCounterRangeArgs {
  /** Index of the first visible row (0-based). */
  startIndex: number;
  /** Index of the last visible row (0-based). */
  endIndex: number;
  /** Total number of results in the data set. */
  totalResults: number;
  /** Query page size. When omitted, the raw visible range is returned. */
  pageSize?: number;
  /** Scroll direction — 'forward' is scrolling down, 'backward' is scrolling up. */
  direction: ScrollDirection;
}

export interface VisibleCounterRange {
  /** 1-based first index shown in the counter. */
  start: number;
  /** 1-based last index shown in the counter (clamped to totalResults). */
  end: number;
}

/**
 * Derive the 1-based "start–end" range shown in the infinite-scroll counter.
 *
 * Both numbers are derived from a SINGLE page index so they always move in
 * lockstep by exactly `pageSize` — never the transient double-width range
 * (e.g. "1–100") that the visible window produced when it straddled a page
 * boundary. Which edge of the visible window defines the current page depends
 * on the scroll direction (the leading edge):
 *   - scrolling down (`forward`)  → the bottom edge (`endIndex`), so the counter
 *     advances once it reaches the next page ("when it gets to the end of the
 *     50 records they both jump +50").
 *   - scrolling up (`backward`)   → the top edge (`startIndex`), so the counter
 *     recedes once it reaches the previous page ("when it gets to the top of the
 *     50 records they both jump -50").
 */
export const getVisibleCounterRange = ({
  startIndex,
  endIndex,
  totalResults,
  pageSize,
  direction,
}: VisibleCounterRangeArgs): VisibleCounterRange => {
  if (!pageSize) {
    return { start: startIndex + 1, end: Math.min(endIndex + 1, totalResults) };
  }

  const leadingIndex = direction === 'backward' ? startIndex : endIndex;
  const page = Math.floor(leadingIndex / pageSize);

  return {
    start: page * pageSize + 1,
    end: Math.min((page + 1) * pageSize, totalResults),
  };
};
